import { ApolloError, gql } from '@apollo/client';
import {
  Box,
  Button,
  HStack,
  Menu,
  MenuButton,
  MenuItem,
  MenuList,
  Stack,
  Text,
  VStack,
} from '@chakra-ui/react';
import { t } from 'i18next';
import { useEffect, useState } from 'react';
import { ZteTableFragment } from '~components/organization/ZteTable/__generated__/ZteTable.graphql';
import { FilterOption } from '~components/organization/ZteTable/types';
import { useZteTable } from '~components/organization/ZteTable/useZteTable';
import { ZteTable } from '~components/organization/ZteTable/ZteTable';
import { Page } from '~components/ui/Page';
import { PageActions } from '~components/ui/PageActions';
import { PageContentZte } from '~components/ui/PageContentZte';
import { PageHeadingZte } from '~components/ui/PageHeadingZte';
import { PageLoader } from '~components/ui/PageLoader';
import { useConditionalPolling } from '~components/useConditionalPolling';
import { ZteEnrolmentStatus, ZteFilters } from '~graphql/__generated__/types';
import { HandleApiError } from '~graphql/HandleApiError';
import { isDefined } from '~utils/types';
import { useZtePageQuery } from './__generated__/Index.graphql';
import { PairDevicesModal } from './PairdeviceModal';
import { UnpairDevicesModal } from './UnpairdeviceModal';

const POLL_INTERVAL = 10000;
export type ZteUiFilters = Array<{ id: string; value: FilterOption[] }>;

type ZteOverviewProps = {
  data: ZteTableFragment[];
  dataWithFilters: ZteFilters;
  error: ApolloError | undefined;
  pageIndex: number;
  pageSize: number;
  filters: ZteUiFilters;
  totalRowCount: number;
  search: string;
  handleSetSearch: (searchText: string) => void;
  handleSetPageSize: (pageSize: number) => void;
  handleSetPageIndex: (pageIndex: number) => void;
  handleSetFilters: (filterId: string, newValue: FilterOption[]) => void;
  onSelectedItemsChange: (items: ZteTableFragment[]) => void;
};

type ZteOverviewHeadingProps = {
  selectedItems: ZteTableFragment[];
  onOpenPairModal: () => void;
  onOpenUnpairModal: () => void;
};

function ZteOverviewHeading({
  selectedItems,
  onOpenPairModal,
  onOpenUnpairModal,
}: ZteOverviewHeadingProps) {
  function Dot() {
    return <Box w="5px" h="5px" bg="gray.600" borderRadius="full" />;
  }

  const allPaired =
    selectedItems.length > 0 &&
    selectedItems.every((item) => item.status === ZteEnrolmentStatus.Paired);
  const allUnpaired =
    selectedItems.length > 0 &&
    selectedItems.every((item) => item.status === ZteEnrolmentStatus.Unpaired);

  const actionButtons = (
    <Stack direction="row" spacing={2}>
      <Button
        variant="solid"
        size="sm"
        h="48px"
        colorScheme="blue"
        onClick={onOpenPairModal}
        isDisabled={selectedItems.length === 0 || !allUnpaired}
      >
        {t('pairDevices')}
      </Button>
      <Menu placement="bottom-end">
        <MenuButton
          as={Button}
          aria-label="More options"
          variant="solid"
          borderRadius="md"
          size="lg"
          p={0}
          w="48px"
          h="48px"
          borderColor="gray.300"
          bg="white"
          _hover={{ bg: 'gray.50' }}
        >
          <HStack spacing="4px" justify="center">
            <Dot />
            <Dot />
            <Dot />
          </HStack>
        </MenuButton>
        <MenuList>
          <MenuItem
            p={4}
            fontWeight={'semibold'}
            textColor={'grey.400'}
            onClick={onOpenUnpairModal}
            isDisabled={selectedItems.length === 0 || !allPaired}
          >
            {t('unpairDevices')}
          </MenuItem>
        </MenuList>
      </Menu>
    </Stack>
  );
  return (
    <>
      <PageHeadingZte actions={actionButtons}>{t('zeroTouchEnrolment')}</PageHeadingZte>
    </>
  );
}

function ZteOverview({
  data,
  dataWithFilters,
  pageIndex,
  pageSize,
  filters,
  handleSetPageSize,
  handleSetPageIndex,
  totalRowCount,
  handleSetFilters,
  search,
  handleSetSearch,
  onSelectedItemsChange,
}: ZteOverviewProps) {
  const table = useZteTable({
    data: data,
    pageIndex,
    pageSize,
    totalRowCount,
  });
  const selectedItems = table.selectedFlatRows.map((row) => row.original);
  useEffect(() => {
    onSelectedItemsChange(selectedItems);
  }, [selectedItems, onSelectedItemsChange]);
  return (
    <>
      <PageActions
        left={
          <VStack align="start" spacing="1">
            <Text fontWeight="bold" pb={4} fontSize={25}>
              {t('allZteDevices')}
            </Text>
            <ZteTable.Filter
              table={table}
              data={data}
              dataWithFilters={dataWithFilters}
              filtersState={filters}
              handleSetFilters={handleSetFilters}
            />
            {selectedItems.length > 0 && (
              <Box
                position="relative"
                top={3}
                right={0}
                bg="blue.500"
                color="white"
                px={3}
                fontSize="xs"
                fontWeight="bold"
                borderRadius="xl"
              >
                {selectedItems.length + '/' + data.length}
                {'    '}
                {t('devicesSelected')}
              </Box>
            )}
          </VStack>
        }
        right={<ZteTable.SearchSelector search={search} handleSetSearch={handleSetSearch} />}
      />
      <Box
        boxShadow="elevated"
        border="1px solid"
        borderRadius="md"
        borderColor="gray.100"
        overflowX="auto"
        bgColor="white"
        width={{ base: '500px', md: 'full', lg: 'full' }}
      >
        <ZteTable table={table} />
      </Box>
      <Box display="flex" marginTop="4">
        <Box flex="1">
          <ZteTable.PageSizeSelector
            table={table}
            handleSetPageSize={handleSetPageSize}
            pageSize={pageSize}
          />
        </Box>
        <ZteTable.Pagination
          pageIndex={pageIndex}
          totalRowCount={totalRowCount}
          handleSetPageIndex={handleSetPageIndex}
          pageSize={pageSize}
        />
      </Box>
    </>
  );
}
export function OrganizationZteIndexPage() {
  const [pageSize, setPageSize] = useState(25);
  const [pageIndex, setPageIndex] = useState(0);
  const [search, setSearch] = useState('');
  const [filters, setFilters] = useState<ZteUiFilters>([
    { id: 'customerName', value: [] },
    { id: 'batchName', value: [] },
    { id: 'modelNumber', value: [] },
    { id: 'status', value: [] },
  ]);
  interface TransformedResult {
    customerName: string[];
    batchName: string[];
    modelNumber: string[];
    status: ZteEnrolmentStatus[];
  }
  function transformFiltersData(filters: ZteUiFilters): TransformedResult {
    const transformed: TransformedResult = {
      customerName: [],
      batchName: [],
      modelNumber: [],
      status: [],
    };

    filters.forEach((filter) => {
      switch (filter.id) {
        case 'customerName':
          transformed.customerName = filter.value.map((option) => option.value);
          break;
        case 'batchName':
          transformed.batchName = filter.value.map((option) => option.value);
          break;
        case 'modelNumber':
          transformed.modelNumber = filter.value.map((option) => option.value);
          break;
        case 'status':
          transformed.status = filter.value.map((option) => option.value as ZteEnrolmentStatus);
          break;
        default:
          break;
      }
    });

    return transformed;
  }
  const transformedFilters = transformFiltersData(filters);
  const handleSetPageSize = (pageSize: number) => {
    setPageSize(pageSize);
  };
  const handleSetPageIndex = (pageIndex: number) => {
    setPageIndex(pageIndex);
  };

  const handleSetFilters = (filterId: string, newValue: FilterOption[]) => {
    setFilters((prevFilters) =>
      prevFilters.map((filter) =>
        filter.id === filterId ? { ...filter, value: newValue } : filter,
      ),
    );
  };

  const handleSetSearch = (searchText: string) => {
    setSearch(searchText);
  };

  const [selectedItems, setSelectedItems] = useState<ZteTableFragment[]>([]);

  const selectedEnrolmentIds = selectedItems.map((item) => item.id);

  const [isPairModalOpen, setIsPairModalOpen] = useState(false);
  const [isUnPairModalOpen, setIsUnPairModalOpen] = useState(false);
  const onOpenPairModal = () => {
    if (selectedItems.length > 0) {
      setIsPairModalOpen(true);
    }
  };
  const onOpenUnpairModal = () => {
    if (selectedItems.length > 0) {
      setIsUnPairModalOpen(true);
    }
  };
  const handlePairSuccess = async () => {
    setIsPairModalOpen(false);
    await refetch();
  };
  const handleUnpairSuccess = async () => {
    setIsUnPairModalOpen(false);
    await refetch();
  };
  const handlePairCancel = () => {
    setIsPairModalOpen(false);
  };
  const handleUnpairCancel = () => {
    setIsUnPairModalOpen(false);
  };
  const { data, loading, error, startPolling, stopPolling, refetch } = useZtePageQuery({
    variables: {
      pageNumber: pageIndex + 1,
      pageSize: pageSize,
      searchTerm: search,
      filter: transformedFilters,
    },
  });

  useConditionalPolling({ startPolling, stopPolling, pollInterval: POLL_INTERVAL });

  const totalRowCount = data?.enrolmentsForOrganization.totalCount || 0;
  return (
    <Page title="zte" pageName="zte_overview">
      {!loading && !error && (
        <PageContentZte>
          <ZteOverviewHeading
            selectedItems={selectedItems}
            onOpenPairModal={onOpenPairModal}
            onOpenUnpairModal={onOpenUnpairModal}
          />
        </PageContentZte>
      )}
      <PageContentZte>
        {loading ? (
          <PageLoader />
        ) : error ? (
          <HandleApiError error={error} />
        ) : (
          isDefined(data) && (
            <ZteOverview
              data={data.enrolmentsForOrganization.zteEnrolment as ZteTableFragment[]}
              dataWithFilters={data.enrolmentsForOrganization.zteFilters as ZteFilters}
              error={error}
              pageSize={pageSize}
              pageIndex={pageIndex}
              totalRowCount={totalRowCount}
              filters={filters}
              search={search}
              handleSetSearch={handleSetSearch}
              handleSetPageSize={handleSetPageSize}
              handleSetPageIndex={handleSetPageIndex}
              handleSetFilters={handleSetFilters}
              onSelectedItemsChange={setSelectedItems}
            />
          )
        )}
      </PageContentZte>
      <PairDevicesModal
        isOpen={isPairModalOpen}
        onCancel={handlePairCancel}
        onSuccess={handlePairSuccess}
        enrolmentIds={selectedEnrolmentIds}
      />
      {/* Unpair Devices Modal */}
      <UnpairDevicesModal
        isOpen={isUnPairModalOpen}
        onCancel={handleUnpairCancel}
        onSuccess={handleUnpairSuccess}
        enrolmentIds={selectedEnrolmentIds}
      />
    </Page>
  );
}

OrganizationZteIndexPage.graphql = {
  queries: {
    ZtePage: gql`
      query ZtePage(
        $pageNumber: Int
        $pageSize: Int
        $searchTerm: String
        $filter: ZteEnrolmentFiltersInput
      ) {
        enrolmentsForOrganization(
          pageNumber: $pageNumber
          pageSize: $pageSize
          searchTerm: $searchTerm
          filter: $filter
        ) {
          zteFilters {
            customers {
              id
              name
            }
            batches {
              id
              name
            }
            modelNumbers
          }
          zteEnrolment {
            ...ZteTable
          }
          totalCount
        }
      }
    `,
  },
};
